import {
  Autocomplete,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { IFiltrosProjeto } from "../../../../../../application/models";
import { onlyNumbers } from "../../../../../../utils";
import { useRecortesExtracaoService } from "../../../../../../use-cases/geo-veles";
import { useAppSelector } from "../../../../../hooks/store-hooks";
import { useEffect } from "react";
import { RecorteCBO, RecorteCboFilterPreview } from "./cbo";
import {
  RecorteClasseSocial,
  RecorteClasseSocialPreview,
} from "./classe-social";
import { RecorteContato, RecorteContatoFilterPreview } from "./contato";
import {
  RecorteDataNascimento,
  RecorteDataNascimentoFilterPreview,
} from "./data-nascimento";
import {
  RecorteEscolaridade,
  RecorteEscolaridadeFilterPreview,
} from "./escolaridade";
import {
  RecorteEstadoCivil,
  RecorteEstadoCivilFilterPreview,
} from "./estado-civil";
import { RecorteIdade, RecorteIdadeFilterPreview } from "./idade";
import { RecorteListaCEPFilterPreview } from "./lista-cep";
import {
  RecorteLocalizacao,
  RecorteLocalizacaoFilterPreview,
} from "./localizacao";
import { RecorteProfissao, RecorteProfissaoFilterPreview } from "./profissao";
import {
  RecorteRendaAnual,
  RecorteRendaAnualFilterPreview,
} from "./renda-anual";
import {
  RecorteRendaMensal,
  RecorteRendaMensalFilterPreview,
} from "./renda-mensal";
import { RecorteScore, RecorteScoreFilterPreview } from "./score";
import { RecorteSexo, RecorteSexoFilterPreview } from "./sexo";
import {
  RecorteListasDeduplicacao,
  RecorteListasDeduplicacaoFilterPreview,
} from "./listas-deduplicacao";
import { isEmpty } from "lodash";
import { RecorteListasCboFilterEditPreview, RecorteListasCboFilterEdit } from "./listas-cbo";
import { RecorteListasCidadesFilterEditPreview } from "./listas-cidade";

const rawOptions: (keyof IFiltrosProjeto)[] = [
  "cbo",
  "listasCbo",
  "classeSocial",
  "contato",
  "dataNascimento",
  "escolaridade",
  "estadoCivil",
  "idade",
  "listasCEP",
  "listasDeduplicacao",
  "localizacao",
  "profissao",
  "rendaPresumidaAnual",
  "rendaPresumidaMensal",
  "scoreVeles",
  "sexo",
  "listasCidades"
];

const filterKeysToLabel = (filterKey: keyof IFiltrosProjeto): string => {
  switch (filterKey) {
    case "cbo":
      return "CBO";
    case "classeSocial":
      return "Classe Social";
    case "contato":
      return "Contatos";
    case "dataNascimento":
      return "Data de Nascimento";
    case "escolaridade":
      return "Escolaridade";
    case "estadoCivil":
      return "Estado Civil";
    case "idade":
      return "Idade";
    case "localizacao":
      return "Localização";
    case "profissao":
      return "Profissão";
    case "rendaPresumidaAnual":
      return "Renda Presumida Anual";
    case "rendaPresumidaMensal":
      return "Renda Presumida Mensal";
    case "scoreVeles":
      return "Score de Risco";
    case "sexo":
      return "Sexo";
    case "listasDeduplicacao":
      return "Listas de Deduplicação";
    case "listasCbo":
      return "Listas de CBO";
    default:
      return "";
  }
};

// Opções ordenadas alfabeticamente pelo rótulo
const sortedFilterOptions = rawOptions
  .map((key) => ({
    key,
    label: filterKeysToLabel(key),
  }))
  .filter(opt => opt.label !== "")
  .sort((a, b) => a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" }));

export const EditRecorteProjetoForm = () => {
  // AppSelector
  const { editRecorteExtracaoForm, selectedFilter } = useAppSelector(
    (state) => state.recorteExtracao
  );

  // Hook
  const {
    setEditRecorteFormNome,
    setEditRecorteFormQuantidade,
    setSelectedFilter,
    resetSelectedFilter,
    resetCreateRecorteForm,
    updateRecorteExtracao,
  } = useRecortesExtracaoService();

  // Use Effect
  useEffect(() => {
    resetCreateRecorteForm();
  }, [resetCreateRecorteForm, resetSelectedFilter]);

  const isFilterValid = () => {
    if (!selectedFilter) {
      return false;
    }

    const selectedFilterData =
      editRecorteExtracaoForm.fields.Filtros?.[selectedFilter];

    if (!selectedFilterData) return false;

    if (Array.isArray(selectedFilterData)) {
      return selectedFilterData.length > 0;
    }

    if (typeof selectedFilterData === "object") {
      return Object.values(selectedFilterData).some(
        (value) => value !== undefined && value !== ""
      );
    }

    return selectedFilterData !== undefined && selectedFilterData !== "";
  };

  const isFormValid =
    editRecorteExtracaoForm.fields.Nome.trim() !== '' &&
    editRecorteExtracaoForm.fields.Quantidade > 0 &&
    ((selectedFilter && isFilterValid()) || !isEmpty(editRecorteExtracaoForm.fields.Filtros));

  // Filtra as opções com base nas regras de exclusão mútua
  const availableOptions = sortedFilterOptions.filter((opt) => {
    const optionKey = opt.key;
    if (editRecorteExtracaoForm.fields.Filtros) {
      if (
        !Object.keys(editRecorteExtracaoForm.fields.Filtros).includes(optionKey)
      ) {
        if (optionKey === "cbo") {
          return !Object.keys(editRecorteExtracaoForm.fields.Filtros).includes("profissao");
        } else if (optionKey === "profissao") {
          return !Object.keys(editRecorteExtracaoForm.fields.Filtros).includes("cbo");
        } else if (optionKey === "dataNascimento") {
          return !Object.keys(editRecorteExtracaoForm.fields.Filtros).includes("idade");
        } else if (optionKey === "idade") {
          return !Object.keys(editRecorteExtracaoForm.fields.Filtros).includes("dataNascimento");
        } else if (optionKey === "localizacao") {
          return !Object.keys(editRecorteExtracaoForm.fields.Filtros).includes("listasCEP");
        } else if (optionKey === "listasCEP") {
          return !Object.keys(editRecorteExtracaoForm.fields.Filtros).includes("localizacao");
        } else if (optionKey === "rendaPresumidaAnual") {
          return !Object.keys(editRecorteExtracaoForm.fields.Filtros).includes("rendaPresumidaMensal");
        } else if (optionKey === "rendaPresumidaMensal") {
          return !Object.keys(editRecorteExtracaoForm.fields.Filtros).includes("rendaPresumidaAnual");
        } else if (optionKey === "listasCbo") {
          return !Object.keys(editRecorteExtracaoForm.fields.Filtros).includes("cbo");
        } else {
          return true;
        }
      }
      return true;
    }
    return true;
  });

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.grey[50]
              : theme.palette.grey[900],
          width: "100%",
          padding: 3,
        }}
      >
        <Grid
          container
          spacing={2}
          justifyContent="center"
          alignItems="center"
          px={1}
        >
          <Grid item xs={6}>
            <TextField
              onChange={(e) => {
                setEditRecorteFormNome(e.target.value);
              }}
              error={Boolean(
                editRecorteExtracaoForm.touched &&
                editRecorteExtracaoForm.errors.find(
                  (item) => item.key === "Nome"
                )
              )}
              helperText={
                editRecorteExtracaoForm.errors.find(
                  (item) => item.key === "Nome"
                )?.message
              }
              name="nome"
              fullWidth
              id="nome"
              label="Dê um nome para diferenciar este recorte dos demais"
              InputLabelProps={{ shrink: true }}
              value={editRecorteExtracaoForm.fields.Nome}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              value={
                editRecorteExtracaoForm.fields.Quantidade === 0
                  ? ""
                  : editRecorteExtracaoForm.fields.Quantidade.toLocaleString(
                    "pt-BR"
                  )
              }
              onChange={(e) => {
                const quantity = onlyNumbers(e.target.value);
                setEditRecorteFormQuantidade(Number(quantity));
              }}
              error={Boolean(
                editRecorteExtracaoForm.touched &&
                editRecorteExtracaoForm.errors.find(
                  (item) => item.key === "Quantidade"
                )
              )}
              helperText={
                editRecorteExtracaoForm.errors.find(
                  (item) => item.key === "Quantidade"
                )?.message
              }
              name="quantidade"
              fullWidth
              id="quantidade"
              label="Defina a quantidade de registros que deseja exportar neste recorte"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} mt={2} textAlign="center">
            <Typography
              textTransform="uppercase"
              fontWeight="bold"
              variant="body1"
              color="primary"
            >
              Filtros
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              value={availableOptions.find(opt => opt.key === selectedFilter) ?? undefined}
              onChange={(event, newValue) => {
                if (newValue) {
                  setSelectedFilter(newValue.key);
                }
              }}
              options={availableOptions}
              isOptionEqualToValue={(option, value) => option.key === value?.key}
              getOptionLabel={(option) => option.label}
              fullWidth
              renderInput={(params) => (
                <TextField {...params} label="Selecione o filtro" />
              )}
            />
          </Grid>

          <Grid container display="flex" alignItems="center" wrap="nowrap">
            {selectedFilter === "cbo" && <RecorteCBO />}
            {selectedFilter === "classeSocial" && <RecorteClasseSocial />}
            {selectedFilter === "contato" && <RecorteContato />}
            {selectedFilter === "dataNascimento" && <RecorteDataNascimento />}
            {selectedFilter === "escolaridade" && <RecorteEscolaridade />}
            {selectedFilter === "estadoCivil" && <RecorteEstadoCivil />}
            {selectedFilter === "idade" && <RecorteIdade />}
            {selectedFilter === "localizacao" && <RecorteLocalizacao />}
            {selectedFilter === "profissao" && <RecorteProfissao />}
            {selectedFilter === "rendaPresumidaAnual" && <RecorteRendaAnual />}
            {selectedFilter === "rendaPresumidaMensal" && (<RecorteRendaMensal />)}
            {selectedFilter === "scoreVeles" && <RecorteScore />}
            {selectedFilter === "sexo" && <RecorteSexo />}
            {selectedFilter === "listasDeduplicacao" && (<RecorteListasDeduplicacao />)}
            {selectedFilter === "listasCbo" && <RecorteListasCboFilterEdit />}
          </Grid>

          {editRecorteExtracaoForm.fields.Filtros &&
            Object.keys(editRecorteExtracaoForm.fields.Filtros).length > 0 && (
              <>
                <Grid item xs={12} my={2} textAlign="center">
                  <Typography
                    variant="subtitle1"
                    color="primary"
                    fontWeight="bold"
                    textTransform="uppercase"
                  >
                    Resumo
                  </Typography>
                </Grid>
                <Grid
                  container
                  textAlign="left"
                  spacing={3}
                  mt={0.5}
                  justifyContent={"left"}
                  sx={{
                    margin: "0 auto",
                    paddingX: 2,
                  }}
                >

                  {/* CBOs */}
                  {editRecorteExtracaoForm.fields.Filtros.cbo && (
                    <RecorteCboFilterPreview />
                  )}

                  {editRecorteExtracaoForm.fields.Filtros.listasCbo && (
                    <RecorteListasCboFilterEditPreview
                    />
                  )}

                  {/* CLASSE SOCIAL */}
                  {editRecorteExtracaoForm.fields.Filtros.classeSocial && (
                    <RecorteClasseSocialPreview />
                  )}

                  {/* CONTATO */}
                  {editRecorteExtracaoForm.fields.Filtros.contato && (
                    <RecorteContatoFilterPreview />
                  )}

                  {/* DATA DE NASCIMENTO */}
                  {editRecorteExtracaoForm.fields.Filtros.dataNascimento && (
                    <RecorteDataNascimentoFilterPreview />
                  )}

                  {/* ESCOLARIDADE */}
                  {editRecorteExtracaoForm.fields.Filtros.escolaridade && (
                    <RecorteEscolaridadeFilterPreview />
                  )}

                  {/* ESTADO CIVIL */}
                  {editRecorteExtracaoForm.fields.Filtros.estadoCivil && (
                    <RecorteEstadoCivilFilterPreview />
                  )}

                  {/* IDADE */}
                  {editRecorteExtracaoForm.fields.Filtros.idade && (
                    <RecorteIdadeFilterPreview />
                  )}

                  {/* LISTAS DE CEP */}
                  {editRecorteExtracaoForm.fields.Filtros.listasCEP && (
                    <RecorteListaCEPFilterPreview />
                  )}

                  {/* LISTAS DE DEDUPLICAÇÃO */}
                  {editRecorteExtracaoForm.fields.Filtros
                    .listasDeduplicacao && (
                      <RecorteListasDeduplicacaoFilterPreview />
                    )}

                  {/* LOCALIZAÇÃO */}
                  {editRecorteExtracaoForm.fields.Filtros.localizacao && (
                    <RecorteLocalizacaoFilterPreview />
                  )}

                  {/* PROFISSÃO */}
                  {editRecorteExtracaoForm.fields.Filtros.profissao && (
                    <RecorteProfissaoFilterPreview />
                  )}

                  {/* RENDA PRESUMIDA ANUAL */}
                  {editRecorteExtracaoForm.fields.Filtros
                    .rendaPresumidaAnual && <RecorteRendaAnualFilterPreview />}

                  {/* RENDA PRESUMIDA MENSAL */}
                  {editRecorteExtracaoForm.fields.Filtros
                    .rendaPresumidaMensal && (
                      <RecorteRendaMensalFilterPreview />
                    )}

                  {/* SCORE VELES */}
                  {editRecorteExtracaoForm.fields.Filtros.scoreVeles && (
                    <RecorteScoreFilterPreview />
                  )}

                  {/* SEXO */}
                  {editRecorteExtracaoForm.fields.Filtros.sexo && (
                    <RecorteSexoFilterPreview />
                  )}

                  {/* LISTAS DE CIDADES */}
                  {editRecorteExtracaoForm.fields.Filtros.listasCidades && (
                    <RecorteListasCidadesFilterEditPreview />
                  )}
                </Grid>
              </>
            )}

          <Grid item xs={4}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={!isFormValid}
              onClick={() => {
                updateRecorteExtracao();
              }}
            >
              Atualizar recorte
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};